import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Contact from './components/Contact';
import AboutPage from './components/AboutPage';
import TermsPage from './components/TermsPage';
import PrivacyPage from './components/PrivacyPage';
import StylesPage from './components/StylesPage';
import BusinessCardPage from './components/BusinessCardPage';
import AccountSetupPage from './components/AccountSetupPage';
import ContactUsPage from './components/ContactUsPage';

// Navigation Component
const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <nav className="absolute top-0 left-0 right-0 z-50 bg-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 sm:h-20 items-center">
          <div className="flex-shrink-0 flex items-center">
            <a href="/">
              <img 
                src="/main-logo.png" 
                alt="Banner Day Threads" 
                className="h-12 sm:h-16 opacity-90 hover:opacity-100 transition duration-300 drop-shadow-lg" 
              />
            </a>
          </div>
          <div className="hidden md:block">
            <div className="ml-10 flex items-center space-x-6 lg:space-x-8">
              <a href="/" className="text-black hover:text-gray-700 px-2 lg:px-3 py-2 font-medium drop-shadow-lg text-sm lg:text-base">Home</a>
              <a href="/styles" className="text-black hover:text-gray-700 px-2 lg:px-3 py-2 font-medium drop-shadow-lg text-sm lg:text-base">Our Styles</a>
              <a href="/about" className="text-black hover:text-gray-700 px-2 lg:px-3 py-2 font-medium drop-shadow-lg text-sm lg:text-base">About</a>
              <a href="/contact-us" className="bg-blue-600 text-white px-4 lg:px-6 py-2 rounded-full font-medium hover:bg-blue-700 transition duration-300 shadow-lg text-sm lg:text-base">Get Started</a>
            </div>
          </div>
          <div className="md:hidden flex items-center space-x-3">
            <a 
              href="/contact-us" 
              className="bg-blue-600 text-white px-4 py-2 rounded-full font-medium hover:bg-blue-700 transition duration-300 shadow-lg text-sm"
            >
              Get Started
            </a>
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
              className="text-black hover:text-gray-700 focus:outline-none drop-shadow-lg"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
        {isMobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white bg-opacity-95 rounded-lg backdrop-blur-sm shadow-lg">
              <a href="/" className="block text-black hover:text-gray-700 px-3 py-3 font-medium text-base">Home</a>
              <a href="/styles" className="block text-black hover:text-gray-700 px-3 py-3 font-medium text-base">Our Styles</a>
              <a href="/about" className="block text-black hover:text-gray-700 px-3 py-3 font-medium text-base">About</a>
              <a href="/contact-us" className="block bg-blue-600 text-white px-6 py-3 rounded-full font-medium hover:bg-blue-700 transition duration-300 text-center mx-3 mt-2">Get Started</a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// FadeIn Component for scroll animation
const FadeIn = ({ children, className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return (
    <div 
      ref={ref} 
      className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
    >
      {children}
    </div>
  );
};

// Hero Section with Video Background
const Hero = () => (
  <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
    {/* Background Video */}
    <video 
      className="absolute inset-0 w-full h-full object-cover"
      autoPlay 
      muted 
      loop 
      playsInline
      onError={(e) => console.log('Video error:', e)}
      onLoadStart={() => console.log('Video loading started')}
      onCanPlay={() => console.log('Video can play')}
      onLoadedData={() => console.log('Video loaded')}
      style={{ zIndex: 1 }}
    >
      <source src="/BannerDayThreads.mp4" type="video/mp4" />
    </video>
    
    {/* Fallback gradient background if video fails to load */}
    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600"></div>
    
    {/* Dark overlay for better text readability */}
    <div className="absolute inset-0 bg-black bg-opacity-40"></div>
    
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
      <FadeIn>
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 drop-shadow-lg leading-tight">
          We Craft Headwear That{' '}
          <span className="bg-gradient-to-r from-yellow-300 to-orange-400 bg-clip-text text-transparent drop-shadow-lg block sm:inline">
            Highlights Your Brand
          </span>
        </h1>
      </FadeIn>
      <FadeIn>
        <p className="text-lg sm:text-xl md:text-2xl text-white mb-6 sm:mb-8 max-w-3xl mx-auto drop-shadow-lg px-4">
          Your Brand. Our Craftsmanship. Your Hat.
        </p>
      </FadeIn>

    </div>
  </section>
);

// Trusted By Section - COMMENTED OUT
/*
const TrustedBy = () => (
  <section className="py-8 sm:py-12 bg-gray-100">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-center text-gray-500 text-xs sm:text-sm font-semibold uppercase tracking-wider mb-6 sm:mb-8">
        Trusted by leading brands
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 sm:gap-8 items-center">
        {[
          { src: "https://upload.wikimedia.org/wikipedia/en/c/c7/SavannahBananas.png", alt: "Savannah Bananas" },
          { src: "https://standardrecreation.com/wp-content/uploads/2024/01/logo.svg", alt: "Standard Recreation" },
          { src: "https://www.homefieldapparel.com/cdn/shop/files/Homefield_536a70b6-edf9-4dfd-93e0-e0d39006113c_180x@2x.png?v=1645131790", alt: "Homefield Apparel" },
          { src: "https://antiguaapparelshop.com/cdn/shop/files/Antigua_FINAL_FINALCOLOR_NoTag_220x.png?v=1681933084", alt: "Antigua Apparel" },
          { src: "https://1863fc.com/cdn/shop/files/1863FC-Color-Badge-512x512_150x.png?v=1688477201", alt: "1863 FC" }
        ].map((brand, index) => (
          <div key={index} className="flex justify-center">
            <img 
              src={brand.src} 
              alt={brand.alt} 
              className="h-8 sm:h-10 md:h-12 opacity-60 hover:opacity-100 transition duration-300" 
            />
          </div>
        ))}
      </div>
    </div>
  </section>
);
*/

// Product Card Component
const ProductCard = ({ image, title, price, description, hatId }) => (
  <FadeIn>
    <a 
      href={hatId ? `/styles#${hatId}` : "/styles"}
      className="block bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 h-full flex flex-col"
    >
      <div className="relative h-64 sm:h-80 overflow-hidden">
        <img 
          src={image} 
          alt={title} 
          className="w-full h-full object-cover transition duration-500 hover:scale-110" 
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-70"></div>
        <div className="absolute bottom-0 left-0 p-4 sm:p-6">
          <h3 className="text-xl sm:text-2xl font-bold text-white">{title}</h3>
          <p className="text-blue-200 text-sm sm:text-base">{price}</p>
        </div>
      </div>
      <div className="p-4 sm:p-6 flex-1 flex flex-col">
        <p className="text-gray-600 text-sm sm:text-base leading-relaxed flex-1">{description}</p>
      </div>
    </a>
  </FadeIn>
);

// Products Section
const Products = () => (
  <section id="products" className="py-12 sm:py-20 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12 sm:mb-16">
        <FadeIn>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 px-4">
            Our{' '}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Premium Headwear
            </span>
          </h2>
          <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
          Custom hats, inside and out, designed to showcase your brand with quality materials and expert craftsmanship. We make getting custom headwear easy!
          </p>
        </FadeIn>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
        <ProductCard
          image="/AirFlow.png"
          title="Air Flow"
          price="Lightweight and breathable performance."
          description="Lightweight polyester ripstop design with side and back perforations for maximum breathability. Finished with a solid rope accent and an adjustable snapback for a secure fit."
          hatId="air-flow"
        />
        <ProductCard
          image="/RoadRunner.png"
          title="Road Runner"
          price="Elite performance and mesh."
          description="A modern spin on the classic trucker cap. Built with durable polyester performance fabric, breathable mesh back panels, and a snapback closure for an easy fit."
          hatId="road-runner"
        />
        <ProductCard
          image="/HighPoint.png"
          title="High Point"
          price="Modern fit with everyday versatility."
          description="Built from lightweight polyester ripstop with laser-cut perforations for breathability. The 5-panel design and adjustable snapback deliver a modern fit that's ready for anything."
          hatId="high-point"
        />
      </div>
      <FadeIn>
        <div className="text-center mt-12 sm:mt-16 px-4">
          <a 
            href="/styles" 
            className="inline-flex items-center px-6 sm:px-8 py-3 border border-transparent text-base sm:text-lg font-medium rounded-full text-white bg-blue-600 hover:bg-blue-700 transition duration-300 w-full sm:w-auto justify-center"
          >
            View All Products
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </FadeIn>
    </div>
  </section>
);

// Custom Approach Card Component
const CustomApproachCard = ({ image, title, description }) => (
  <FadeIn>
    <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden">
      <div className="h-48 sm:h-56 overflow-hidden">
        <img 
          src={image}
          alt={title}
          className="w-full h-full object-cover transition duration-500 hover:scale-105"
        />
      </div>
      <div className="p-6 text-center">
        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">{title}</h3>
        <p className="text-gray-600 text-sm sm:text-base leading-relaxed">{description}</p>
      </div>
    </div>
  </FadeIn>
);

// Custom Approach Section
const CustomApproach = () => (
  <section className="py-12 sm:py-20 bg-gray-100">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12 sm:mb-16">
        <FadeIn>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 px-4">
            Our{' '}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Fully Custom Approach
            </span>
          </h2>
          <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Every aspect of your hat, inside and out, can be customized to perfectly represent your brand.
          </p>
        </FadeIn>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
        <CustomApproachCard
          image="/PurpleHat.png"
          title="Custom Colors"
          description="Choose from our extensive color library or match your exact brand colors."
        />
        <CustomApproachCard
          image="/Valhalla.png"
          title="Branding Options"
          description="Embroidery, patches, woven labels, or printed designs - we do it all."
        />
        <CustomApproachCard
          image="/WhiteHat.png"
          title="Materials & Fit"
          description="Select from premium fabrics and choose the perfect fit for your audience."
        />
        <CustomApproachCard
          image="/Interior.png"
          title="Interior Details"
          description="Custom sweatbands, lining, and interior tags complete your branded experience."
        />
      </div>
    </div>
  </section>
);

// Features Section
const FeatureItem = ({ icon, title, description }) => (
  <FadeIn>
    <div className="flex">
      <div className="flex-shrink-0">
        <div className="flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-md bg-blue-100 text-blue-600">
          <div className="text-lg sm:text-xl">{icon}</div>
        </div>
      </div>
      <div className="ml-3 sm:ml-4">
        <h3 className="text-base sm:text-lg font-medium text-gray-900">{title}</h3>
        <p className="text-gray-600 text-sm sm:text-base">{description}</p>
      </div>
    </div>
  </FadeIn>
);

// Features Section
const Features = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  // Placeholder showcase images
  const showcaseImages = [
    "/C1.jpg",
    "/C2.jpg",
    "/C3.jpg",
    "/C5.jpg"
      ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % showcaseImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + showcaseImages.length) % showcaseImages.length);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    // Prevent default scrolling behavior during swipe
    e.preventDefault();
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) nextSlide();
    if (isRightSwipe) prevSlide();
  };

  return (
    <section className="py-12 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Image Carousel - Full Width */}
        <div className="relative mb-12 sm:mb-16">
          <div 
            className="relative h-64 sm:h-80 md:h-96 lg:h-[500px] rounded-xl overflow-hidden cursor-grab active:cursor-grabbing touch-pan-x"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{ touchAction: 'pan-x' }}
          >
            {/* Carousel Images */}
            <div className="relative w-full h-full">
              {showcaseImages.map((image, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-500 ${
                    index === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  {/* Left click area */}
                  <div 
                    className="absolute left-0 top-0 w-1/3 h-full z-20 cursor-pointer"
                    onClick={prevSlide}
                  />
                  
                  {/* Right click area */}
                  <div 
                    className="absolute right-0 top-0 w-1/3 h-full z-20 cursor-pointer"
                    onClick={nextSlide}
                  />
                  
                  <img 
                    src={image}
                    alt={`Custom hats showcase ${index + 1}`}
                    className="w-full h-full object-cover relative z-10"
                  />
                </div>
              ))}
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 sm:p-3 shadow-lg transition-all duration-300"
            >
              <svg className="w-4 h-4 sm:w-6 sm:h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 sm:p-3 shadow-lg transition-all duration-300"
            >
              <svg className="w-4 h-4 sm:w-6 sm:h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Slide Indicators */}
            <div className="absolute bottom-3 sm:bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
              {showcaseImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-colors duration-300 ${
                    index === currentSlide ? 'bg-white' : 'bg-white bg-opacity-50'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Text Content Below Carousel */}
        <div className="text-center mb-8 sm:mb-12">
          <FadeIn>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
              Why Choose{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Banner Day Threads?
              </span>
            </h2>
          </FadeIn>
        </div>

        {/* Feature Items Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-6xl mx-auto">
          <FadeIn>
            <div className="text-center p-4 h-full flex flex-col">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 leading-tight">Expert Design Team</h3>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed flex-1 flex items-center justify-center">25+ years of industry experience bringing your custom hat designs to life.</p>
            </div>
          </FadeIn>
          <FadeIn>
            <div className="text-center p-4 h-full flex flex-col">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 leading-tight">Fast & Flexible Production</h3>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed flex-1 flex items-center justify-center">In-house fabric stock and exclusive production space for quick turnarounds.</p>
            </div>
          </FadeIn>
          <FadeIn>
            <div className="text-center p-4 h-full flex flex-col">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 leading-tight">Dedicated Support</h3>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed flex-1 flex items-center justify-center">Work directly with the team creating your custom headwear.</p>
            </div>
          </FadeIn>
          <FadeIn>
            <div className="text-center p-4 h-full flex flex-col">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 leading-tight">Latest Trends & Materials</h3>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed flex-1 flex items-center justify-center">Stay current with cutting-edge styles and premium materials.</p>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

// Testimonial Card Component
const TestimonialCard = ({ image, name, title, quote, rating }) => (
  <FadeIn>
    <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
      <div className="flex items-center mb-4">
        <div className="flex-shrink-0">
          <img className="h-10 w-10 sm:h-12 sm:w-12 rounded-full" src={image} alt={name} />
        </div>
        <div className="ml-3 sm:ml-4">
          <h3 className="text-base sm:text-lg font-medium text-gray-900">{name}</h3>
          <p className="text-gray-500 text-sm sm:text-base">{title}</p>
        </div>
      </div>
      <p className="text-gray-600 mb-4 text-sm sm:text-base leading-relaxed">{quote}</p>
      <div className="flex text-yellow-400">
        {[...Array(Math.floor(rating))].map((_, i) => (
          <svg key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-current" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    </div>
  </FadeIn>
);

// Testimonials Section
const Testimonials = () => (
  <section className="py-12 sm:py-20 bg-gray-100">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12 sm:mb-16">
        <FadeIn>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 px-4">
            What Our{' '}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Clients Say
            </span>
          </h2>
          <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Don't just take our word for it - hear from brands we've worked with.
          </p>
        </FadeIn>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
        <TestimonialCard
          image="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
          name="Sarah Johnson"
          title="Marketing Director, Summit Outdoors"
          quote="The custom hats we ordered from Banner Day Threads became our most popular promotional item last year. The quality is exceptional and our customers love them."
          rating={5}
        />
        <TestimonialCard
          image="https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
          name="Michael Chen"
          title="CEO, Urban Brew Co."
          quote="We've used several suppliers in the past, but Banner Day Threads stands out for their attention to detail and willingness to go the extra mile to get our branding just right."
          rating={5}
        />
        <TestimonialCard
          image="https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
          name="David Rodriguez"
          title="Events Manager, TechForward"
          quote="For our annual conference, we needed 500 custom hats in just two weeks. Not only did they deliver on time, but the quality exceeded our expectations."
          rating={5}
        />
      </div>
    </div>
  </section>
);

// Footer Section
const Footer = () => (
  <footer className="bg-gray-900 text-white pt-12 sm:pt-16 pb-6 sm:pb-8">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
        <div className="sm:col-span-2 lg:col-span-1">
          <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-4 sm:mb-6">
            Banner Day Threads
          </h3>
          <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
            We craft premium custom headwear that fully highlights your brand. Not ours.
          </p>
        </div>
        <div>
          <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Products</h4>
          <ul className="space-y-1 sm:space-y-2">
            {['Snapbacks', 'Rope Hats', 'Trucker Hats', 'Bucket Hats', 'Visors'].map((item, index) => (
              <li key={index}>
                <span className="text-gray-400 text-sm sm:text-base">{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Company</h4>
          <ul className="space-y-1 sm:space-y-2">
            <li>
              <a href="/about" className="text-gray-400 hover:text-white transition-colors duration-300 text-sm sm:text-base">
                About
              </a>
            </li>
            <li>
              <a href="/contact-us" className="text-gray-400 hover:text-white transition-colors duration-300 text-sm sm:text-base">
                Contact Us
              </a>
            </li>
          </ul>
        </div>
        {/* <div>
          <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Legal</h4>
          <ul className="space-y-1 sm:space-y-2">
            <li>
              <a href="/terms" className="text-gray-400 hover:text-white transition-colors duration-300 text-sm sm:text-base">
                Terms of Service
              </a>
            </li>
            <li>
              <a href="/privacy" className="text-gray-400 hover:text-white transition-colors duration-300 text-sm sm:text-base">
                Privacy Policy
              </a>
            </li>
          </ul>
        </div> */}
      </div>
      <div className="border-t border-gray-800 mt-8 sm:mt-12 pt-6 sm:pt-8 flex flex-col md:flex-row justify-between items-center">
        <p className="text-gray-400 mb-4 md:mb-0 text-sm sm:text-base text-center md:text-left">
          © 2025 Banner Day Threads, LLC. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// Home Page Component (Main Landing Page)
const HomePage = () => {
  return (
    <div className="font-sans bg-gray-50">
      <Navigation />
      <Hero />
      {/* <TrustedBy /> - COMMENTED OUT */}
      <Products />
      <CustomApproach />
      <Features />
      <Contact />
      <Footer />
    </div>
  );
};

// Main App Component with Routing
const App = () => {
  return (
    <Router>
      <Routes>
        {/* Main application routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/styles" element={<StylesPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/accountsetup" element={<AccountSetupPage />} />
        <Route path="/contact-us" element={<ContactUsPage />} />
        
        {/* Redirect old about-us URL to new about URL */}
        <Route path="/about-us" element={<Navigate to="/about" replace />} />
        
        {/* Business Card Routes - Keep the original URLs for QR codes */}
        <Route path="/scott" element={<BusinessCardPage />} />
        <Route path="/eric" element={<BusinessCardPage />} />
        <Route path="/quayla" element={<BusinessCardPage />} />
        <Route path="/caleb" element={<BusinessCardPage />} />
        <Route path="/nancy" element={<BusinessCardPage />} />
      </Routes>
    </Router>
  );
};

export default App;